import { keyframes } from '@emotion/react';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, Drawer, IconButton, Typography, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@/features/auth';
import { SystemBanner } from '@/shared/components/SystemBanner';
import { Toaster } from '@/shared/components/Toaster';
import { useEvents } from '@/shared/hooks/useEvents';
import { NavRail } from './NavRail';
import { SkipLink } from './SkipLink';

const enter = keyframes({ from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'none' } });

export function AppShell() {
  const { user, signOut } = useSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const compact = useMediaQuery((t: Theme) => t.breakpoints.between('md', 'lg'));
  useEvents(!!user);
  if (!user) return null;
  const props = { role: user.role, name: user.name, onSignOut: () => { void signOut().then(() => navigate('/login')); } };
  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <SkipLink />
      <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'sticky', top: 0, height: '100dvh', flexShrink: 0 }}><NavRail {...props} compact={compact} /></Box>
      <Drawer open={open} onClose={() => setOpen(false)} sx={{ display: { md: 'none' } }}><NavRail {...props} onNavigate={() => setOpen(false)} /></Drawer>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <SystemBanner />
        <Box sx={(t) => ({ display: { xs: 'flex', md: 'none' }, position: 'sticky', top: 0, zIndex: 'appBar', alignItems: 'center', gap: 1, px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(t.palette.background.default, 0.85), backdropFilter: 'saturate(1.4) blur(10px)' })}>
          <IconButton aria-label="Open menu" onClick={() => setOpen(true)}><MenuIcon /></IconButton>
          <Typography component="p" sx={{ fontSize: '1.125rem', fontWeight: 700, lineHeight: 1, letterSpacing: '-0.035em' }}>Screenline</Typography>
        </Box>
        <Box component="main" id="main" tabIndex={-1} sx={{ px: { xs: 2, md: 4 }, pt: { xs: 2.5, md: 4 }, pb: { xs: 5, md: 7 }, maxWidth: 1440, mx: 'auto', outline: 'none' }}>
          <Box key={pathname} sx={{ animation: `${enter} 420ms cubic-bezier(0.16, 1, 0.3, 1) both` }}><Outlet /></Box>
        </Box>
      </Box>
      <Toaster />
    </Box>
  );
}
